// Stale-bundle detection. The client bundle is stamped with VITE_APP_VERSION at
// build time (vite.config.ts → import.meta.env.VITE_APP_VERSION). The server
// reports its build ID on every response via the X-App-Version header and via
// GET /api/version. When they differ, this running bundle is stale → prompt the
// user to reload (UpdateAvailableBanner). Non-React module so queryClient.ts can
// feed it — observers subscribe via onUpdateAvailable (mirrors redirectToLogin).

export const CLIENT_BUILD_ID = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "";

let updateAvailable = false;
let lastServerId: string | null = null;
let dismissedBuildId: string | null = null;
const listeners = new Set<() => void>();

/**
 * Compare a server-reported build id to this bundle's. On mismatch, mark the
 * update available and notify subscribers.
 *
 * s94 (Stephanie F16): once a tester DISMISSES the banner, stop re-nagging for the
 * SAME server build — only re-notify when a DIFFERENT (newer) build id appears. This
 * keeps the stale-bundle protection (a fresh deploy still prompts a reload) while
 * ending the within-session repeat that read as "the message won't go away."
 */
export function reportServerBuildId(serverId: string | null | undefined): void {
  if (!serverId || !CLIENT_BUILD_ID) return; // nothing to compare → ignore
  if (serverId === CLIENT_BUILD_ID) return; // up to date
  lastServerId = serverId;
  updateAvailable = true;
  if (serverId === dismissedBuildId) return; // already dismissed for this build → stay quiet
  listeners.forEach((l) => l());
}

export function isUpdateAvailable(): boolean {
  return updateAvailable && lastServerId !== dismissedBuildId;
}

/** Record that the user dismissed the prompt for the currently-known server build,
 *  so it won't re-show until a newer build id is reported. */
export function dismissUpdate(): void {
  dismissedBuildId = lastServerId;
}

export function onUpdateAvailable(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Force a one-time reload to pick up the current bundle. Used on the hard-error
 * path — a failed API call while the bundle is known-stale, where the user is
 * effectively stuck and has nothing to lose. No-ops unless there's a real
 * mismatch, and guards against reload loops by only reloading once per server
 * build id (a reload that doesn't resolve the mismatch won't loop).
 */
export function forceReloadForStaleBundle(serverId: string | null | undefined): void {
  if (typeof window === "undefined") return;
  if (!serverId || !CLIENT_BUILD_ID || serverId === CLIENT_BUILD_ID) return;
  const key = `oe:reloaded-for:${serverId}`;
  try {
    if (sessionStorage.getItem(key)) return; // already reloaded for this version → don't loop
    sessionStorage.setItem(key, "1");
  } catch {
    /* sessionStorage unavailable → fall through and still reload once */
  }
  window.location.reload();
}
